import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowLeft, ExternalLink, Database, FlaskConical } from 'lucide-react'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Skeleton } from '../components/ui/Skeleton'
import { HelpTooltip } from '../components/ui/Tooltip'
import { InlineCopy } from '../components/ui/CopyButton'
import { ApiError } from '../components/ui/ApiError'
import { useProteinPdbHits } from '../api/hooks'
import { getPdbUrl } from '../utils/externalLinks'
import { getGlossaryEntry } from '../utils/glossary'

// Get ligand structure image URL from RCSB CDN
function getLigandImageUrl(ligandId: string): string {
  const firstLetter = ligandId.charAt(0).toUpperCase()
  return `https://cdn.rcsb.org/images/ccd/unlabeled/${firstLetter}/${ligandId}.svg`
}

// Parse formula into JSX with subscripts (like old Perl layout)
function FormulaDisplay({ formula }: { formula: string | undefined }) {
  if (!formula) return <span className="text-gray-500">-</span>

  const parts: React.ReactNode[] = []
  // Split by spaces to handle formulas like "C6 H12 O6"
  const tokens = formula.split(/\s+/)

  tokens.forEach((token, tokenIdx) => {
    let i = 0
    while (i < token.length) {
      const char = token[i]
      if (/\d/.test(char)) {
        let num = ''
        while (i < token.length && /\d/.test(token[i])) {
          num += token[i]
          i++
        }
        parts.push(<sub key={`${tokenIdx}-sub-${num}-${i}`}>{num}</sub>)
      } else {
        parts.push(<span key={`${tokenIdx}-char-${i}`}>{char}</span>)
        i++
      }
    }
    // Add space between tokens except for last one
    if (tokenIdx < tokens.length - 1) {
      parts.push(<span key={`space-${tokenIdx}`}> </span>)
    }
  })

  return <>{parts}</>
}

interface PdbHit {
  hit_num: number
  hsp_num: number
  pdb_id: string
  chain_id?: string
  title?: string
  evalue: number
  identity: number
  cover: number
  pdb_url: string
  ligands?: Ligand[]
  pdb_count: number  // Total PDB structures in this hit
  pdb_index: number  // Index of this PDB structure (0-based)
}

interface Ligand {
  ligand_id: string
  name?: string
  formula?: string
  pubchem_cid?: string
  smiles?: string
}

interface PdbHitsResponse {
  hits: PdbHit[]
}

export function PdbHitsPage() {
  const { clusterId, proteinId } = useParams<{ clusterId: string; proteinId: string }>()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'entries' | 'ligands'>('entries')

  // Get domain-level filter params if present
  const resStartParam = searchParams.get('res_start')
  const resEndParam = searchParams.get('res_end')
  const resStart = resStartParam ? parseInt(resStartParam, 10) : undefined
  const resEnd = resEndParam ? parseInt(resEndParam, 10) : undefined
  const isDomainFiltered = resStart !== undefined && resEnd !== undefined

  const { data, isLoading, error } = useProteinPdbHits(
    proteinId || '',
    clusterId || '',
    resStart,
    resEnd
  ) as {
    data: PdbHitsResponse | undefined
    isLoading: boolean
    error: Error | null
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <Link
          to={`/proteins/${clusterId}/${proteinId}`}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {proteinId}
        </Link>
        <ApiError
          error={error}
          message="Failed to load PDB hits"
          onRetry={() => window.location.reload()}
        />
      </div>
    )
  }

  const hits = data.hits || []

  // Collect all unique ligands from all hits with count and best E-value
  const ligandStats: Record<string, { ligand: Ligand; count: number; bestEvalue: number }> = {}
  hits.forEach((hit) => {
    if (hit.ligands && hit.ligands.length > 0) {
      hit.ligands.forEach((ligand) => {
        if (ligandStats[ligand.ligand_id]) {
          ligandStats[ligand.ligand_id].count++
          if (hit.evalue < ligandStats[ligand.ligand_id].bestEvalue) {
            ligandStats[ligand.ligand_id].bestEvalue = hit.evalue
          }
        } else {
          ligandStats[ligand.ligand_id] = { ligand, count: 1, bestEvalue: hit.evalue }
        }
      })
    }
  })
  // Sort by best E-value ascending, then by count descending
  const allLigands = Object.values(ligandStats).sort((a, b) => {
    if (a.bestEvalue !== b.bestEvalue) return a.bestEvalue - b.bestEvalue
    return b.count - a.count
  })

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Link
        to={`/proteins/${clusterId}/${proteinId}`}
        className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to {proteinId}
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-white">
          PDB BLAST Hits
          {isDomainFiltered && (
            <span className="text-lg font-normal text-gray-400 ml-3">
              (Domain {resStart}-{resEnd})
            </span>
          )}
        </h1>
        <p className="text-gray-400 mt-2">
          <span className="font-mono text-accent-cyan">{proteinId}</span>
          {' '}from cluster{' '}
          <Link to={`/clusters/${clusterId}`} className="font-mono text-accent-cyan hover:underline">
            {clusterId}
          </Link>
        </p>
        <Badge variant="amber" size="md" className="mt-3">
          {hits.length} PDB hits found
        </Badge>
      </motion.div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        <button
          onClick={() => setActiveTab('entries')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'entries'
              ? 'bg-accent-cyan/20 text-accent-cyan border-b-2 border-accent-cyan'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Database className="w-4 h-4 inline mr-2" />
          PDB Entries ({hits.length})
        </button>
        <button
          onClick={() => setActiveTab('ligands')}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === 'ligands'
              ? 'bg-accent-amber/20 text-accent-amber border-b-2 border-accent-amber'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FlaskConical className="w-4 h-4 inline mr-2" />
          Bound Ligands ({allLigands.length})
        </button>
      </div>

      {/* Content */}
      {activeTab === 'entries' ? (
        <Card>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-3 text-gray-400 font-medium w-14">Hit</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium w-14">HSP</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium">Title</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">PDB ID</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Chain ID</th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">
                      <span className="inline-flex items-center gap-1">
                        E-value
                        <HelpTooltip content={getGlossaryEntry('evalue')?.definition || 'Expect value'} />
                      </span>
                    </th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">
                      <span className="inline-flex items-center gap-1">
                        Identity
                        <HelpTooltip content={getGlossaryEntry('identity')?.definition || 'Sequence identity'} />
                      </span>
                    </th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">
                      <span className="inline-flex items-center gap-1">
                        Cover
                        <HelpTooltip content={getGlossaryEntry('coverage')?.definition || 'Query coverage'} />
                      </span>
                    </th>
                    <th className="text-left py-2 px-3 text-gray-400 font-medium w-32">Ligand</th>
                  </tr>
                </thead>
                <tbody>
                  {hits.map((hit, idx) => {
                    // Calculate row class based on hit_num for consistent striping
                    const rowClass = hit.hit_num % 2 === 1 ? 'bg-white/[0.02]' : ''
                    const isFirstRow = hit.pdb_index === 0
                    const rowSpan = hit.pdb_count

                    return (
                      <tr key={idx} className={`border-b border-white/5 ${rowClass}`}>
                        {/* Hit and HSP columns - only show on first row of each hit */}
                        {isFirstRow && (
                          <>
                            <td className="py-3 px-3 text-gray-500" rowSpan={rowSpan}>{hit.hit_num}</td>
                            <td className="py-3 px-3 text-gray-500" rowSpan={rowSpan}>{hit.hsp_num}</td>
                          </>
                        )}
                        {/* Title, PDB ID, Chain - always shown (different per PDB) */}
                        <td className="py-3 px-3 text-gray-300 max-w-md truncate" title={hit.title}>
                          {hit.title || '-'}
                        </td>
                        <td className="py-3 px-3">
                          {hit.pdb_id ? (
                            <InlineCopy text={hit.pdb_id}>
                              <a
                                href={getPdbUrl(hit.pdb_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-accent-cyan hover:underline inline-flex items-center gap-1"
                              >
                                {hit.pdb_id}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </InlineCopy>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-gray-400 font-mono text-xs">
                          {hit.chain_id || '-'}
                        </td>
                        {/* E-value, Identity, Cover - only show on first row */}
                        {isFirstRow && (
                          <>
                            <td className="py-3 px-3 font-mono text-gray-400 text-xs" rowSpan={rowSpan}>
                              {hit.evalue.toExponential(2)}
                            </td>
                            <td className="py-3 px-3" rowSpan={rowSpan}>
                              <Badge variant={hit.identity >= 90 ? 'green' : hit.identity >= 50 ? 'amber' : 'default'} size="sm">
                                {hit.identity.toFixed(1)}%
                              </Badge>
                            </td>
                            <td className="py-3 px-3 font-mono text-gray-400 text-xs" rowSpan={rowSpan}>
                              {hit.cover.toFixed(1)}%
                            </td>
                          </>
                        )}
                        {/* Ligand - always shown (different per PDB) */}
                        <td className="py-3 px-3">
                          {hit.ligands && hit.ligands.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {hit.ligands.map((ligand, lIdx) => (
                                <a
                                  key={lIdx}
                                  href={`https://www.rcsb.org/ligand/${ligand.ligand_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-mono text-accent-amber hover:underline text-xs"
                                >
                                  {ligand.ligand_id}
                                </a>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            {allLigands.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Ligand</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">Number</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-24">Best E-value</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium">Name</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-32">Formula</th>
                      <th className="text-left py-2 px-3 text-gray-400 font-medium w-20">PubChem</th>
                      <th className="text-center py-2 px-3 text-gray-400 font-medium w-24">Structure</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allLigands.map((item, idx) => {
                      const rowClass = idx % 2 === 0 ? 'bg-white/[0.02]' : ''
                      return (
                        <>
                          {/* Main row */}
                          <tr key={`ligand-${idx}`} className={`border-b border-white/5 ${rowClass}`}>
                            <td className="py-3 px-3">
                              <a
                                href={`https://www.rcsb.org/ligand/${item.ligand.ligand_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-accent-amber hover:underline"
                              >
                                {item.ligand.ligand_id}
                              </a>
                            </td>
                            <td className="py-3 px-3 text-gray-300">{item.count}</td>
                            <td className="py-3 px-3 font-mono text-gray-400 text-xs">
                              {item.bestEvalue.toExponential(2)}
                            </td>
                            <td className="py-3 px-3 text-gray-300">{item.ligand.name || '-'}</td>
                            <td className="py-3 px-3 text-gray-400 font-mono text-xs">
                              <FormulaDisplay formula={item.ligand.formula} />
                            </td>
                            <td className="py-3 px-3">
                              {item.ligand.pubchem_cid ? (
                                <a
                                  href={`https://pubchem.ncbi.nlm.nih.gov/compound/${item.ligand.pubchem_cid}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-accent-purple hover:underline text-xs"
                                >
                                  {item.ligand.pubchem_cid}
                                </a>
                              ) : (
                                <span className="text-gray-500">-</span>
                              )}
                            </td>
                            <td className="py-3 px-3 text-center" rowSpan={2}>
                              <a
                                href={`https://www.rcsb.org/ligand/${item.ligand.ligand_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block"
                              >
                                <img
                                  src={getLigandImageUrl(item.ligand.ligand_id)}
                                  alt={item.ligand.ligand_id}
                                  className="w-16 h-16 object-contain bg-white rounded mx-auto"
                                  loading="lazy"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none'
                                  }}
                                />
                              </a>
                            </td>
                          </tr>
                          {/* SMILES row */}
                          <tr key={`smiles-${idx}`} className={`border-b border-white/10 ${rowClass}`}>
                            <td colSpan={6} className="py-2 px-3">
                              {item.ligand.smiles ? (
                                <div className="text-xs font-mono text-gray-500 break-all max-w-xl">
                                  {item.ligand.smiles}
                                </div>
                              ) : (
                                <span className="text-gray-500 text-xs">SMILES not available</span>
                              )}
                            </td>
                          </tr>
                        </>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No bound ligands found in PDB hits</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
